import { faArrowsLeftRight } from '@fortawesome/pro-solid-svg-icons/faArrowsLeftRight';
import { faArrowsUpDown } from '@fortawesome/pro-solid-svg-icons/faArrowsUpDown';
import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter';
import { faTruckFast } from '@fortawesome/pro-solid-svg-icons/faTruckFast';
import { faWeightScale } from '@fortawesome/pro-solid-svg-icons/faWeightScale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInfiniteShippingCostPresets } from '@ukpostbox/client-data';
import {
	Button,
	ButtonGroup,
	ButtonSubmit,
	ColourStateSolid,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	ContentBlock,
	CountryCodeControl,
	FormWrapperLabel,
	SelectItemType,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { ConvertCmToInches, ConvertGramsToKilo } from '../../_Global/Utils/UnitConversion';
import { ShippingPresetItemType } from '../Types/ShippingOptionsTypes';
import { ShippingOptions } from '../ShippingOptions/ShippingOptions';

const defaultShipping = {
	weight: 0.0,
	length: 0.0,
	depth: 0.0,
	width: 0.0,
	countryCode: 'GB',
};

function ShippingCalculator() {
	const params = useParams();

	const { t } = useTranslation();
	const { isTiny, isSmall } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();

	const { data: presetsData, status: presetsStatus, error: presetsError } = useInfiniteShippingCostPresets();

	const formMethods = useForm({
		defaultValues: defaultShipping,
		mode: 'onChange',
	});

	const [shippingValues, setShippingValues] = useState<any>(null);
	const [updateShipping, setUpdateShipping] = useState<boolean>(false);
	const [shapedData, setShapedData] = useState<Array<SelectItemType>>([]);
	const [weightUnit, setWeightUnit] = useState<string>('G');
	const [weightStep, setWeightStep] = useState<number>(1);
	const [measurementUnit, setMeasurementUnit] = useState<string>('CM');
	const [measurementStep, setMeasurementStep] = useState<number>(0.01);

	const CheckAndShapeData = (weight: number, width: number, length: number, depth: number, fromPreset?: boolean) => ({
		weight: weightUnit === 'KG' ? ConvertGramsToKilo(weight, fromPreset ?? false) : weight,
		width: measurementUnit === 'IN' ? ConvertCmToInches(width, fromPreset ?? false) : width,
		length: measurementUnit === 'IN' ? ConvertCmToInches(length, fromPreset ?? false) : length,
		depth: measurementUnit === 'IN' ? ConvertCmToInches(depth, fromPreset ?? false) : depth,
		submitWeight: weightUnit === 'KG' ? ConvertGramsToKilo(weight, true) : weight,
		submitWidth: measurementUnit === 'IN' ? ConvertCmToInches(width, true) : width,
		submitLength: measurementUnit === 'IN' ? ConvertCmToInches(length, true) : length,
		submitDepth: measurementUnit === 'IN' ? ConvertCmToInches(depth, true) : depth,
		countryCode: formMethods.getValues().countryCode,
	});

	const UpdatePackageValues = (passedParams: Record<string, any>, fromPreset?: boolean) => {
		const newParams =
			Object.keys(passedParams).length > 0
				? passedParams
				: {
						weight: 0,
						width: 0,
						height: 0,
						depth: 0,
					};

		const parsedData = CheckAndShapeData(
			newParams.weight,
			newParams.width,
			newParams.height,
			newParams.depth,
			fromPreset,
		);

		formMethods.setValue('weight', parsedData.submitWeight);
		formMethods.setValue('width', parsedData.submitWidth);
		formMethods.setValue('length', parsedData.submitLength);
		formMethods.setValue('depth', parsedData.submitDepth);
		formMethods.trigger();

		if (Object.keys(passedParams).length > 0) {
			setUpdateShipping(!updateShipping);
		} else {
			setShippingValues(null);
		}
	};

	const SetPresetData = (option: number, fromPreset?: boolean) => {
		const item: ShippingPresetItemType = presetsData?.pages.map(
			(page: any) =>
				page.data.filter(
					(dataItem: ShippingPresetItemType) => dataItem.mailItemSizeDescription === shapedData[option].label,
				)[0],
		)[0];

		if (item) {
			UpdatePackageValues(item, fromPreset);
		}
	};

	const ShapePresetsData = () => {
		const selectOptions: Array<SelectItemType> = [];

		if (!presetsData) return selectOptions;

		presetsData.pages.map((page: any) =>
			page.data.map((item: ShippingPresetItemType, index: number) =>
				selectOptions.push({ label: item.mailItemSizeDescription, value: index }),
			),
		);

		return selectOptions;
	};

	const ConvertWeight = () => {
		setWeightStep(measurementUnit !== 'G' ? 0.0001 : 1);
		const newWeight = ConvertGramsToKilo(formMethods.getValues('weight'), weightUnit !== 'G');
		formMethods.setValue('weight', newWeight);

		formMethods.trigger();
	};

	const ConvertMeasurements = () => {
		setMeasurementStep(measurementUnit !== 'CM' ? 0.01 : 0.01);
		const newWidth = ConvertCmToInches(formMethods.getValues('width'), measurementUnit !== 'CM');
		const newLength = ConvertCmToInches(formMethods.getValues('length'), measurementUnit !== 'CM');
		const newDepth = ConvertCmToInches(formMethods.getValues('depth'), measurementUnit !== 'CM');

		formMethods.setValue('width', newWidth);
		formMethods.setValue('length', newLength);
		formMethods.setValue('depth', newDepth);

		formMethods.trigger();
	};

	const HandleSend = (data: any) => {
		setShippingValues(CheckAndShapeData(data.weight, data.width, data.length, data.depth));
		setUpdateShipping(!updateShipping);
	};

	useEffect(() => {
		ConvertWeight();
	}, [weightUnit]);

	useEffect(() => {
		ConvertMeasurements();
	}, [measurementUnit]);

	useEffect(() => {
		if (presetsData) {
			setShapedData(ShapePresetsData());
		}
	}, [presetsData]);

	useEffect(() => {
		if (params) {
			UpdatePackageValues(params);
		}
	}, [params]);

	useEffect(() => {
		updatePageMeta({
			title: t('shippingCalculator.title'),
		});
	}, [currentLocale]);

	return (
		<div className='content-main content-area-narrow '>
			<div className='container-narrow pt-lg '>
				<h1>{t('shippingCalculator.title')}</h1>

				<div className='row mb-lg'>
					<div className='col-xs-12 col-lg-6 col-xl-5'>
						<FormProvider {...formMethods}>
							<form onSubmit={formMethods.handleSubmit((data, e) => HandleSend(data))} className='sticky'>
								<fieldset>
									<h5 className='mb-0'>{t('shippingCalculator.labels.destination')}</h5>
									<p>{t('shippingCalculator.labels.destinationDescription')}</p>

									<div className='mb-xl'>
										<Controller
											control={formMethods.control}
											name='countryCode'
											rules={{ required: true }}
											render={({ field }) => (
												<FormWrapperLabel label={t('shippingCalculator.labels.countryCode')}>
													<CountryCodeControl
														placeholder={t(
															'shippingCalculator.controls.countryCodePlaceholder',
														)}
														name='countryCode'
														displayType={ComboBoxTypeEnum.Single}
														displayItemLabelType={ComboBoxItemDisplayEnum.Label}
														defaultValue={[field.value]}
														forwardValueCallback={(val) => field.onChange(val)}
													/>
												</FormWrapperLabel>
											)}
										/>
									</div>

									<h5 className='mb-0'>{t('shippingCalculator.labels.itemSize')}</h5>
									<p>{t('shippingCalculator.labels.itemSizeDescription')}</p>

									<div className='flex-column flex-gap-md mb-md'>
										{presetsStatus === 'error' ? (
											<ContentBlock
												error
												title={presetsError.name}
												content={presetsError.message}
											/>
										) : (
											<FormWrapperLabel label={t('shippingCalculator.labels.itemPresets')}>
												<ComboBoxControl
													name='shippingPresets'
													placeholder={t(
														'shippingCalculator.controls.itemPresetsPlaceholder',
													)}
													valueType='number'
													data={shapedData}
													loading={
														shapedData === null ||
														presetsStatus === 'pending' ||
														presetsError !== null
													}
													forwardValueCallback={(e) => SetPresetData(e, true)}
													displayType={ComboBoxTypeEnum.Single}
													displayItemLabelType={ComboBoxItemDisplayEnum.Label}
												/>
											</FormWrapperLabel>
										)}

										<div
											className={`${!isTiny && !isSmall ? 'flex-row' : 'flex-column'} flex-gap-sm`}
										>
											<FormWrapperLabel
												label={t('shippingCalculator.labels.weight')}
												invalid={
													formMethods.formState.errors.weight &&
													formMethods.formState.errors.weight?.message !== ''
												}
												validationMessage={
													formMethods.formState.errors.weight?.message as string
												}
											>
												<div className='input-addon addon-first'>
													<div title={weightUnit} className='input-addon-icon'>
														<FontAwesomeIcon icon={faWeightScale} />
													</div>
													<input
														type='number'
														step={weightStep}
														aria-invalid={
															formMethods.formState.errors.weight ? 'true' : 'false'
														}
														{...formMethods.register('weight', {
															required: 'This is a required field',
															min: 0.0001,
														})}
													/>
												</div>
											</FormWrapperLabel>

											<div className='form-group'>
												<label>
													{weightUnit === 'G' ? t('units.grams') : t('units.kilograms')}
												</label>
												<ButtonGroup singleRow>
													<Button
														squared
														title={t('units.gramsShort')}
														onClick={() => setWeightUnit('G')}
														colour={
															weightUnit === 'G'
																? ColourStateSolid.Selected
																: ColourStateSolid.Secondary
														}
													/>
													<Button
														squared
														title={t('units.kilogramsShort')}
														onClick={() => setWeightUnit('KG')}
														colour={
															weightUnit === 'KG'
																? ColourStateSolid.Selected
																: ColourStateSolid.Secondary
														}
													/>
												</ButtonGroup>
											</div>
										</div>

										<div
											className={`${!isTiny && !isSmall ? 'flex-row' : 'flex-column'} flex-gap-sm`}
										>
											<FormWrapperLabel
												label={t('shippingCalculator.labels.width')}
												invalid={
													formMethods.formState.errors.width &&
													formMethods.formState.errors.width?.message !== ''
												}
												validationMessage={
													formMethods.formState.errors.width?.message as string
												}
											>
												<div className='input-addon addon-first'>
													<div title={measurementUnit} className='input-addon-icon'>
														<FontAwesomeIcon icon={faArrowsLeftRight} />
													</div>
													<input
														type='number'
														step={measurementStep}
														aria-invalid={
															formMethods.formState.errors.width ? 'true' : 'false'
														}
														{...formMethods.register('width', {
															required: 'This is a required field',
															min: 0.1,
														})}
													/>
												</div>
											</FormWrapperLabel>

											<FormWrapperLabel
												label={t('shippingCalculator.labels.height')}
												invalid={
													formMethods.formState.errors.depth &&
													formMethods.formState.errors.depth?.message !== ''
												}
												validationMessage={
													formMethods.formState.errors.depth?.message as string
												}
											>
												<div className='input-addon addon-first'>
													<div title={measurementUnit} className='input-addon-icon'>
														<FontAwesomeIcon icon={faArrowsUpDown} />
													</div>
													<input
														type='number'
														step={measurementStep}
														aria-invalid={
															formMethods.formState.errors.depth ? 'true' : 'false'
														}
														{...formMethods.register('depth', {
															required: 'This is a required field',
															min: 0.1,
														})}
													/>
												</div>
											</FormWrapperLabel>

											<FormWrapperLabel
												label={t('shippingCalculator.labels.depth')}
												invalid={
													formMethods.formState.errors.length &&
													formMethods.formState.errors.length?.message !== ''
												}
												validationMessage={
													formMethods.formState.errors.length?.message as string
												}
											>
												<div className='input-addon addon-first'>
													<div title={measurementUnit} className='input-addon-icon'>
														<FontAwesomeIcon
															icon={faArrowUpRightAndArrowDownLeftFromCenter}
														/>
													</div>
													<input
														type='number'
														step={measurementStep}
														aria-invalid={
															formMethods.formState.errors.length ? 'true' : 'false'
														}
														{...formMethods.register('length', {
															required: 'This is a required field',
															min: 0.1,
														})}
													/>
												</div>
											</FormWrapperLabel>

											<div className='form-group'>
												<label>
													{measurementUnit === 'CM'
														? t('units.centimeter')
														: t('units.inches')}
												</label>

												<ButtonGroup singleRow>
													<Button
														squared
														title={t('units.centimeterShort')}
														onClick={() => setMeasurementUnit('CM')}
														colour={
															measurementUnit === 'CM'
																? ColourStateSolid.Selected
																: ColourStateSolid.Secondary
														}
													/>
													<Button
														squared
														title={t('units.inchesShort')}
														onClick={() => setMeasurementUnit('IN')}
														colour={
															measurementUnit === 'IN'
																? ColourStateSolid.Selected
																: ColourStateSolid.Secondary
														}
													/>
												</ButtonGroup>
											</div>
										</div>
									</div>

									<ButtonSubmit
										title={
											updateShipping
												? t('shippingCalculator.actions.updateRates')
												: t('shippingCalculator.actions.getRates')
										}
										disabled={!formMethods.formState.isValid}
										fullWidth
										colour={ColourStateSolid.Primary}
									/>
								</fieldset>
							</form>
						</FormProvider>
					</div>

					<div className='col-xs-12 col-lg-6 col-xl-7'>
						{shippingValues && formMethods.formState.isValid ? (
							<ShippingOptions
								updateValues={updateShipping}
								width={shippingValues.width}
								depth={shippingValues.depth}
								length={shippingValues.length}
								weight={shippingValues.weight}
								countryCode={shippingValues.countryCode}
							/>
						) : (
							<ContentBlock
								stacked
								iconContent={{ icon: faTruckFast }}
								title={t('shippingCalculator.noResults.title')}
								content={t('shippingCalculator.noResults.description')}
								showBorder
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default ShippingCalculator;
